import { useEffect, useRef, useState } from "react";
import { api, apiError, BACKEND_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Sparkles, Upload as UploadIcon, Loader2, CheckCircle2, Video } from "lucide-react";

export default function LessonEditor({ lesson, courseId, onClose }) {
  const [form, setForm] = useState({ ...lesson, takeaways_text: (lesson.takeaways || []).join("\n"), video_url: lesson.video_url || "", video_provider: lesson.video_provider || "" });
  const [saving, setSaving] = useState(false);
  const [heygen, setHeygen] = useState(null);
  const [job, setJob] = useState(lesson.heygen || null);
  const [avatarId, setAvatarId] = useState("");
  const [voiceId, setVoiceId] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();
  const changed = useRef(false);
  const set = (k, v) => setForm({ ...form, [k]: v });

  useEffect(() => {
    if (!lesson.id) return;
    api.get("/admin/heygen/options").then(({ data }) => {
      setHeygen(data);
      setAvatarId(data.default_avatar_id || data.avatars[0]?.id || "");
      setVoiceId(data.default_voice_id || data.voices[0]?.id || "");
    }).catch(() => setHeygen({ avatars: [], voices: [], error: true }));
  }, [lesson.id]);

  useEffect(() => {
    if (!lesson.id || !job || ["completed", "failed"].includes(job.status)) return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/admin/lessons/${lesson.id}/video/heygen`);
        setJob(data);
        if (data.status === "completed") { changed.current = true; toast.success("HeyGen video is ready and attached"); setForm((f) => ({ ...f, video_provider: "heygen", video_url: `/api/lessons/${lesson.id}/video.mp4` })); }
        if (data.status === "failed") toast.error(`HeyGen failed: ${data.error || "unknown error"}`);
      } catch {}
    }, 15000);
    return () => clearInterval(t);
  }, [job, lesson.id]);

  const payload = () => ({
    title: form.title, module_id: form.module_id, content: form.content, cpd_minutes: Number(form.cpd_minutes) || 15,
    takeaways: form.takeaways_text.split("\n").map((s) => s.trim()).filter(Boolean),
    video_provider: form.video_provider || null, video_url: form.video_url || null,
  });

  const save = async () => {
    setSaving(true);
    try {
      if (lesson.id) await api.put(`/admin/lessons/${lesson.id}`, payload());
      else await api.post(`/admin/courses/${courseId}/lessons`, payload());
      toast.success("Lesson saved"); onClose(true);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post(`/admin/lessons/${lesson.id}/video/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, video_provider: "upload", video_url: data.video_url })); changed.current = true; toast.success("Video uploaded");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setUploading(false); }
  };

  const generate = async () => {
    if (!window.confirm("Render this lesson as a HeyGen avatar video? This uses HeyGen credits and takes a few minutes.")) return;
    try {
      const { data } = await api.post(`/admin/lessons/${lesson.id}/video/heygen`, { avatar_id: avatarId, voice_id: voiceId });
      setJob(data); toast.success("HeyGen is rendering. You can close this and come back.");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open onOpenChange={() => onClose(changed.current)}>
      <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto" data-testid="lesson-editor-dialog">
        <DialogHeader><DialogTitle>{lesson.id ? "Edit lesson" : "New lesson"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-[1fr_120px]">
            <Input value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Lesson title" data-testid="lesson-title-input" />
            <Input type="number" min={5} max={180} value={form.cpd_minutes} onChange={(e) => set("cpd_minutes", e.target.value)} placeholder="CPD minutes" data-testid="lesson-minutes-input" />
          </div>
          <div>
            <div className="mb-1 text-xs font-medium text-muted-foreground">Lesson text (HTML allowed: &lt;p&gt;, &lt;strong&gt;, &lt;h3&gt;, &lt;ul&gt;&lt;li&gt;)</div>
            <Textarea rows={12} value={form.content} onChange={(e) => set("content", e.target.value)} className="font-mono text-xs" data-testid="lesson-content-input" />
          </div>
          <div>
            <div className="mb-1 text-xs font-medium text-muted-foreground">Key takeaways (one per line, used on the summary PDF)</div>
            <Textarea rows={3} value={form.takeaways_text} onChange={(e) => set("takeaways_text", e.target.value)} data-testid="lesson-takeaways-input" />
          </div>

          <div className="rounded-xl border border-border p-4">
            <div className="mb-3 flex items-center gap-2 font-semibold"><Video className="h-4 w-4" /> Video</div>
            <div className="flex flex-wrap gap-2">
              {[["", "None (audio narration)"], ["youtube", "YouTube link"], ["vimeo", "Vimeo link"], ["upload", "Uploaded file"], ["heygen", "HeyGen avatar"]].map(([v, l]) => (
                <button key={v} type="button" onClick={() => set("video_provider", v)} data-testid={`video-provider-${v || "none"}`}
                  className={`rounded-full border px-3 py-1 text-xs ${form.video_provider === v ? "border-[#1779AC] bg-[#1779AC] text-white" : "border-border text-muted-foreground"}`}>{l}</button>
              ))}
            </div>
            {(form.video_provider === "youtube" || form.video_provider === "vimeo") && (
              <Input className="mt-3" value={form.video_url} onChange={(e) => set("video_url", e.target.value)} placeholder="Paste the video link" data-testid="lesson-video-url-input" />
            )}
            {form.video_provider === "upload" && (
              <div className="mt-3">
                {!lesson.id ? <p className="text-xs text-muted-foreground">Save the lesson first, then upload a video.</p> : (
                  <div className="flex items-center gap-3">
                    <input ref={fileRef} type="file" accept="video/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="lesson-video-file-input" />
                    <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-video-button">
                      {uploading ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <UploadIcon className="mr-1.5 h-3.5 w-3.5" />} {uploading ? "Uploading…" : "Choose MP4"}
                    </Button>
                    {form.video_url && lesson.video_file_id && <span className="inline-flex items-center gap-1 text-xs text-emerald-600"><CheckCircle2 className="h-3.5 w-3.5" /> Video attached</span>}
                  </div>
                )}
              </div>
            )}
            {form.video_provider === "heygen" && (
              <div className="mt-3 space-y-3">
                {!lesson.id ? <p className="text-xs text-muted-foreground">Save the lesson first, then generate the video.</p> : !heygen ? <p className="text-xs text-muted-foreground">Loading HeyGen account…</p> : heygen.error ? <p className="text-xs text-destructive">HeyGen is not reachable. Check the API key.</p> : (
                  <>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div>
                        <div className="mb-1 text-xs font-medium text-muted-foreground">Avatar</div>
                        <select value={avatarId} onChange={(e) => setAvatarId(e.target.value)} className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm" data-testid="heygen-avatar-select">
                          {heygen.avatars.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                        </select>
                      </div>
                      <div>
                        <div className="mb-1 text-xs font-medium text-muted-foreground">Voice</div>
                        <select value={voiceId} onChange={(e) => setVoiceId(e.target.value)} className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm" data-testid="heygen-voice-select">
                          {heygen.voices.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                        </select>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-3">
                      <Button size="sm" className="btn-brand" onClick={generate} disabled={job && !["completed", "failed"].includes(job.status)} data-testid="heygen-generate-button">
                        <Sparkles className="mr-1.5 h-3.5 w-3.5" /> {job?.status === "completed" ? "Re-render video" : "Generate video from lesson text"}
                      </Button>
                      <span className="text-xs text-muted-foreground">{heygen.remaining_credits} HeyGen credits left</span>
                      {job && <span className="inline-flex items-center gap-1 text-xs" data-testid="heygen-status">
                        {["completed"].includes(job.status) ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> : job.status === "failed" ? null : <Loader2 className="h-3.5 w-3.5 animate-spin" />} {job.status}
                      </span>}
                    </div>
                  </>
                )}
              </div>
            )}
            {form.video_url && lesson.id && (form.video_provider === "upload" || form.video_provider === "heygen") && lesson.video_file_id && (
              <video controls preload="metadata" className="mt-3 w-full rounded-lg bg-black" src={`${BACKEND_URL}${form.video_url}`} crossOrigin="use-credentials" data-testid="lesson-video-preview" />
            )}
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onClose(changed.current)}>Cancel</Button>
            <Button className="btn-brand" onClick={save} disabled={saving || !form.title.trim()} data-testid="save-lesson-button">{saving ? "Saving…" : "Save lesson"}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
